import { useState } from 'react';
import { Button, Card, CardContent } from '../ui';
import { api } from '../../services/api.service';
import { useAuth } from '../../stores/auth.store';

interface ScoreSubmissionResult {
  message: string;
  score: number;
  previousHighScore: number | null;
  isNewHighScore: boolean;
  rank?: number;
  gameId: string;
}

interface ScoreSubmissionProps {
  gameId: string;
  score: number;
  onSubmitted?: (result: ScoreSubmissionResult) => void;
  onClose?: () => void;
  autoSubmit?: boolean;
}

export function ScoreSubmission({
  gameId,
  score,
  onSubmitted,
  onClose,
  autoSubmit = false,
}: ScoreSubmissionProps) {
  const { isAuthenticated } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<ScoreSubmissionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const submitScore = async () => {
    if (!isAuthenticated) {
      setError('Please sign in to submit your score');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await api.post<ScoreSubmissionResult>(
        `/highscores/${gameId}`,
        { score }
      );
      setResult(response.data);
      setHasSubmitted(true);
      onSubmitted?.(response.data);
    } catch (err) {
      setError('Failed to submit score. Please try again.');
      console.error('Score submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Auto-submit on mount if enabled
  useState(() => {
    if (autoSubmit && isAuthenticated && !hasSubmitted) {
      submitScore();
    }
  });

  if (result) {
    return (
      <Card className="text-center">
        <CardContent className="py-6">
          {result.isNewHighScore ? (
            <>
              <div className="text-4xl mb-2">🎉</div>
              <h3 className="text-xl font-bold text-white mb-2">
                New High Score!
              </h3>
              <p className="text-3xl font-mono text-purple-400 mb-2">
                {result.score.toLocaleString()}
              </p>
              {result.rank && (
                <p className="text-slate-300">
                  You&apos;re now ranked{' '}
                  <span className="text-purple-400 font-bold">
                    #{result.rank}
                  </span>
                </p>
              )}
              {result.previousHighScore && (
                <p className="text-sm text-slate-400 mt-2">
                  Previous best: {result.previousHighScore.toLocaleString()}
                </p>
              )}
            </>
          ) : (
            <>
              <div className="text-4xl mb-2">👍</div>
              <h3 className="text-xl font-bold text-white mb-2">Good Game!</h3>
              <p className="text-2xl font-mono text-slate-300 mb-2">
                {result.score.toLocaleString()}
              </p>
              <p className="text-slate-400">
                Your high score:{' '}
                <span className="text-purple-400">
                  {result.currentHighScore?.toLocaleString() ||
                    result.previousHighScore?.toLocaleString()}
                </span>
              </p>
            </>
          )}
          {onClose && (
            <Button onClick={onClose} className="mt-4">
              Continue
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="text-center">
      <CardContent className="py-6">
        <h3 className="text-xl font-bold text-white mb-2">Game Over</h3>
        <p className="text-3xl font-mono text-purple-400 mb-4">
          {score.toLocaleString()}
        </p>

        {error && <p className="text-red-400 text-sm mb-4">{error}</p>}

        {isAuthenticated ? (
          <Button
            onClick={submitScore}
            isLoading={isSubmitting}
            disabled={hasSubmitted}
          >
            {hasSubmitted ? 'Submitted!' : 'Submit Score'}
          </Button>
        ) : (
          <div className="space-y-3">
            <p className="text-slate-400 text-sm">
              Sign in to save your score to the leaderboard
            </p>
            <Button variant="outline" onClick={onClose}>
              Continue as Guest
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Extend result type to include currentHighScore which API returns when not a new high score
interface ScoreSubmissionResult {
  message: string;
  score: number;
  previousHighScore: number | null;
  isNewHighScore: boolean;
  rank?: number;
  gameId: string;
  currentHighScore?: number;
}
